import { Countries } from '@platform/client';
import { OrganizationApplicationParameters } from '@platform/client/schemas';
import { FormsInputDropdown, InlineFormInput } from '@platform/components';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ApplicationPageProps } from '../../Application';
import { useApplicationLocalStorage } from '../../application-hook';
import { ApplicationBackground } from '../../ApplicationBackground';
import { ApplicationPageContent } from '../../components/ApplicationPageContent';
import { OrgDetailsShowcase } from './OrgDetailsShowcase';

type FormValues = {
  orgName: string;
  country: string;
  registrationYear: number;
};

// Use fetch here so we can call inside of submit function
const checkOrganizationNameExists = async (organizationName: string) => {
  const orgNameExistsEndpoint = import.meta.env.VITE_API_HOST + '/application/organization-exists';
  const response = await fetch(
    orgNameExistsEndpoint + `?organization_name=${encodeURIComponent(organizationName)}`,
    {
      credentials: 'include',
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    },
  );
  if (response.status === 200) {
    const responseData = await response.json();
    return responseData.exists;
  } else {
    return false;
  }
};

export const OrgDetailsSelection = (props: ApplicationPageProps) => {
  const { onBack, onContinue, progress, dataKey } = { ...props };

  const { updateApplicationData, getApplicationDataByKey } =
    useApplicationLocalStorage<OrganizationApplicationParameters>(dataKey);

  const {
    register,
    control,
    handleSubmit,
    setError,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      orgName: getApplicationDataByKey('name'),
      country: getApplicationDataByKey('hqCountry')?.toString() || '',
      registrationYear: getApplicationDataByKey('yearOfRegistration'),
    },
  });

  const countriesList = Countries.useGetCountries();

  const countryOptions =
    countriesList.isSuccess ?
      countriesList.data.map((country) => ({
        text: country.name,
        textValue: country.id.toString(),
      }))
    : [];

  const submit: SubmitHandler<FormValues> = async (data) => {
    const orgNameExists = await checkOrganizationNameExists(data.orgName);
    if (orgNameExists) {
      setError('orgName', {
        type: 'manual',
        message:
          'An organization with this name already exists. Try using a different name or check if your organization is already registered.',
      });
      return;
    } else {
      clearErrors('orgName');
    }
    updateApplicationData({
      name: data.orgName,
      hqCountry: Number(data.country),
      yearOfRegistration: data.registrationYear,
    });
    onContinue();
  };

  const handleBack = () => {
    updateApplicationData({
      name: watch('orgName'),
      hqCountry: Number(watch('country')),
      yearOfRegistration: watch('registrationYear'),
    });
    onBack();
  };

  return (
    <ApplicationBackground
      progress={progress}
      isOrganization={true}
      onBack={handleBack}
      onSubmit={handleSubmit(submit)}
      customLeftPanelContent={<OrgDetailsShowcase />}
      isEditProfile={props.isEditProfile}
    >
      <ApplicationPageContent
        title="Let's get started with your organization's profile"
        description={
          "We'll begin with a few basics to help build a profile. You'll be able to edit this later if needed."
        }
      >
        <InlineFormInput
          label='Organization name'
          placeholder="Enter your organization's full name"
          register={() =>
            register('orgName', {
              required: "Enter your organization's full name to continue",
            })
          }
          errors={errors.orgName}
        />
        <FormsInputDropdown
          control={control}
          name='country'
          rules={{ required: 'Select the country your headquarters is located in to continue' }}
          label='Country where your organization is headquartered'
          placeholder='Start typing country name'
          items={countryOptions}
          type='default'
          error={errors.country}
        />
        <InlineFormInput
          label='Year your organization was registered'
          inputType='number'
          placeholder='Enter year your organization was registered'
          register={() =>
            register('registrationYear', {
              required: 'Enter the year your organization was registered to continue',
            })
          }
          errors={errors.registrationYear}
        />
      </ApplicationPageContent>
    </ApplicationBackground>
  );
};
